import { minToLaunchFor, type ActivityOption } from '@otryad/shared';
import { useNavigate } from 'react-router';

import { useHaptic } from '@/app/TmaProvider';
import { LevelPicker } from '@/components/LevelPicker';
import { Button, Card, Chip, ChipGroup, MainButton, StatusBadge, Toggle } from '@/components/ui';
import { ScrollHint } from '@/components/ScrollHint';
import { cn } from '@/lib/cn';
import type { OnboardingDraft } from '@/lib/draft';
import { fmt, texts } from '@/lib/texts';

import { Section } from './Section';
import { OnboardingScreen, OptionsState, useOptions } from './shared';

const t = texts.onboarding.activities;

function sizeHint(code: string, size: number): string | null {
  const hints = t.sizeHint as Record<string, string>;
  return hints[`${code}${size}`] ?? null;
}

export function sizeLabel(size: number): string {
  if (size === 2) return t.sizePair;
  if (size === 4) return t.sizeFour;
  return fmt(t.sizeOption, { size });
}

/** Карточка основной активности: выбор, размеры, минимум запуска, запасной. */
function ActivityCard({
  activity,
  selected,
  squadSize,
  onSelect,
  onSize,
}: {
  activity: ActivityOption;
  selected: boolean;
  squadSize: number | null | undefined;
  onSelect: () => void;
  onSize: (size: number) => void;
}) {
  const sizes = activity.sizeOptions;
  const size = selected ? (squadSize ?? null) : null;
  return (
    <div
      className={cn(
        'rounded-card bg-surface overflow-hidden transition',
        selected && 'ring-accent/70 ring-1',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        // Выбор был виден только кружком-картинкой; экранный диктор его не различал (раунд 3, N10)
        aria-pressed={selected}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-base font-semibold">{activity.name}</span>
            {activity.source === 'PROPOSED' ? <StatusBadge tone="accent">new</StatusBadge> : null}
          </span>
          {activity.description ? (
            <span className="text-muted mt-0.5 block text-sm leading-snug">
              {activity.description}
            </span>
          ) : null}
        </span>
        <span
          aria-hidden="true"
          className={cn(
            'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
            selected ? 'border-accent' : 'border-border',
          )}
        >
          {selected ? <span className="bg-accent h-3 w-3 rounded-full" /> : null}
        </span>
      </button>
      {selected ? (
        <div className="border-border flex flex-col gap-3 border-t px-4 py-3">
          {sizes.length > 1 ? (
            <div className="flex flex-col gap-2">
              <span className="text-muted text-xs font-semibold tracking-wide uppercase">
                {t.sizeLabel}
              </span>
              {/* Раунд 4, N8: выбор одного размера — переключатель, а не чекбоксы */}
              <ChipGroup radio label={t.sizeLabel}>
                {sizes.map((s) => (
                  <Chip
                    key={s}
                    single
                    selected={size === s}
                    onClick={() => onSize(s)}
                    meta={sizeHint(activity.code, s)}
                  >
                    {sizeLabel(s)}
                  </Chip>
                ))}
              </ChipGroup>
            </div>
          ) : null}
          {size ? (
            <p className="text-muted text-sm">
              {/* Число рядом с выбранным размером читалось как размер отряда: «выбрал 8, а тут 6».
                  Поэтому в строке стоят оба числа и сказано, что значит меньшее. */}
              {fmt(t.minLaunch, { size, min: minToLaunchFor(size, activity.minPlay) })}
              {activity.reserveSlots > 0 && activity.rotationHint
                ? ` · ${fmt(t.reserveHint, { hint: activity.rotationHint })}`
                : ''}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Шаг 4 — основная активность, размер, уровень, интересы; «Предложить свою». */
export function ActivitiesScreen() {
  return (
    <OnboardingScreen step={4} title={t.title} subtitle={t.subtitle}>
      {({ draft, update }) => <Body draft={draft} update={update} />}
    </OnboardingScreen>
  );
}

function Body({
  draft,
  update,
}: {
  draft: OnboardingDraft;
  update: (p: Partial<OnboardingDraft>) => void;
}) {
  const options = useOptions(draft.cityId);
  const navigate = useNavigate();
  const haptic = useHaptic();
  const primaries = options.data?.primaryActivities ?? [];
  const interests = options.data?.interestActivities ?? [];
  const hasPending = (options.data?.pendingProposalIds.length ?? 0) > 0;

  const primary = primaries.find((a) => a.id === draft.primaryActivityId) ?? null;
  const level = primary ? (draft.levels[primary.id] ?? null) : null;
  const waiting = hasPending && draft.waitingForProposal;

  const selectPrimary = (a: ActivityOption) => {
    haptic.selection();
    const extras = { ...draft.extraChoices };
    delete extras[a.id];
    update({
      primaryActivityId: a.id,
      squadSize: a.sizeOptions.length === 1 ? a.sizeOptions[0] : null,
      waitingForProposal: false,
      extraChoices: extras,
    });
  };

  /** Второй набор: та же активность, что основная, второй раз не заводится. */
  const extraIds = Object.keys(draft.extraChoices);
  const extraActivity = primaries.find((a) => a.id === extraIds[0]) ?? null;
  const extraSize = extraActivity ? draft.extraChoices[extraActivity.id] : null;
  const extraLevel = extraActivity ? (draft.levels[extraActivity.id] ?? null) : null;
  const extraNeedsLevel = Boolean(extraActivity?.needsLevel) && extraLevel === null;

  const selectExtra = (a: ActivityOption) => {
    haptic.selection();
    update({
      extraChoices: { [a.id]: a.sizeOptions.length === 1 ? a.sizeOptions[0]! : null },
    });
  };
  const clearExtra = () => {
    haptic.selection();
    update({ extraChoices: {} });
  };

  const setLevel = (activityId: string, lvl: number) =>
    update({ levels: { ...draft.levels, [activityId]: lvl } });

  const toggleInterest = (id: string) => {
    haptic.selection();
    const cur = draft.interestActivityIds;
    update({ interestActivityIds: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  const needLevel = Boolean(primary?.needsLevel) && level === null;
  const primaryReady = Boolean(primary && draft.squadSize && !needLevel);
  // Второй набор — дело добровольное, но начатый выбор нужно довести: без размера и уровня
  // заявку завести нельзя, и «Дальше» ждёт (кнопка зеленеет только на полностью готовом шаге).
  const extraReady = !extraActivity || Boolean(extraSize && !extraNeedsLevel);
  const ready = (primaryReady || waiting) && extraReady;
  const hint =
    !primary && !waiting
      ? t.needPrimary
      : primary && !draft.squadSize
        ? t.needSize
        : needLevel
          ? t.needLevel
          : extraActivity && !extraSize
            ? t.needExtraSize
            : extraNeedsLevel
              ? t.needExtraLevel
              : null;

  return (
    <OptionsState query={options}>
      {hasPending ? (
        <Card title={t.pendingTitle}>
          <p className="text-muted mb-2 text-sm">{t.pendingBody}</p>
          <Toggle
            checked={draft.waitingForProposal}
            onChange={(on) =>
              update({
                waitingForProposal: on,
                ...(on ? { primaryActivityId: null, squadSize: null } : {}),
              })
            }
            label={t.skipPrimary}
            hint={draft.waitingForProposal ? t.skipPrimaryOn : undefined}
          />
        </Card>
      ) : null}

      <Section n={1} title={t.stepPrimary} hint={t.stepPrimaryHint}>
        {primaries.map((a) => (
          <ActivityCard
            key={a.id}
            activity={a}
            selected={!waiting && draft.primaryActivityId === a.id}
            squadSize={draft.squadSize}
            onSelect={() => selectPrimary(a)}
            onSize={(s) => {
              haptic.selection();
              update({ squadSize: s });
            }}
          />
        ))}
        <Button variant="ghost" onClick={() => void navigate('/pool/propose')}>
          {t.proposeCta}
        </Button>
      </Section>

      {primary && !waiting && primary.needsLevel ? (
        <Section n={2} title={fmt(t.levelFor, { name: primary.name })} hint={t.stepLevelHint}>
          <Card>
            <LevelPicker
              activityCode={primary.code}
              value={level}
              onChange={(l) => setLevel(primary.id, l)}
            />
          </Card>
        </Section>
      ) : null}

      {primary && !waiting ? (
        <Section
          n={primary.needsLevel ? 3 : 2}
          title={t.stepExtra}
          hint={t.stepExtraHint}
        >
          {primaries
            .filter((a) => a.id !== primary.id)
            .filter((a) => !extraActivity || a.id === extraActivity.id)
            .map((a) => (
              <ActivityCard
                key={a.id}
                activity={a}
                selected={extraActivity?.id === a.id}
                squadSize={extraSize}
                onSelect={() => (extraActivity?.id === a.id ? clearExtra() : selectExtra(a))}
                onSize={(s) => {
                  haptic.selection();
                  update({ extraChoices: { [a.id]: s } });
                }}
              />
            ))}
          {extraActivity ? (
            <>
              {extraActivity.needsLevel ? (
                <Card title={fmt(t.levelFor, { name: extraActivity.name })}>
                  <LevelPicker
                    activityCode={extraActivity.code}
                    value={extraLevel}
                    onChange={(l) => setLevel(extraActivity.id, l)}
                  />
                </Card>
              ) : null}
              <Button variant="ghost" onClick={clearExtra}>
                {t.extraRemove}
              </Button>
            </>
          ) : null}
        </Section>
      ) : null}

      {interests.length > 0 ? (
        <Section
          n={primary && !waiting ? (primary.needsLevel ? 4 : 3) : 2}
          title={t.stepInterests}
          hint={t.interestsHint}
        >
          <Card>
            <ChipGroup>
            {interests.map((a) => (
              <Chip
                key={a.id}
                selected={draft.interestActivityIds.includes(a.id)}
                onClick={() => toggleInterest(a.id)}
              >
                {a.name}
                </Chip>
              ))}
            </ChipGroup>
          </Card>
        </Section>
      ) : null}

      {hint ? <p className="text-muted px-1 text-sm">{hint}</p> : null}

      <ScrollHint />

      <MainButton
        text={texts.common.next}
        enabled={ready}
        onClick={() => {
          update({ step: 4 });
          void navigate('/onboarding/about');
        }}
      />
    </OptionsState>
  );
}
